import { Auth, type SignInBusy, type SignInError } from "@bundle/core";
import { useEffect, useState, type ReactNode } from "react";
import { flowErrorText, PasskeyStep, PasswordStep, ResetCodeStep, ResetDoneStep, ResetPasswordStep, StepError, TwoFactorStep } from "./SignInSteps.tsx";
import { Button, Divider, Icon, Stack, Text, TextField } from "../primitives/index.ts";
import { useT, type Translate } from "../i18n/I18nProvider.tsx";
import { useSession } from "../hooks/index.ts";

export type SignInFormProps = {
  /** Where an OAuth redirect comes back to (the current page, so invites/desktop sign-in continue). */
  returnTo: string;
  /** Prefilled dev-login email (mock mode). */
  defaultDevEmail?: string;
};

export function signInErrorText(t: Translate, error: SignInError): string {
  switch (error) {
    case "wrongPassword":
      return t("auth.error.wrongPassword");
    case "locked":
      return t("auth.error.locked", { minutes: 15 });
    case "twoStepCode":
      return t("auth.error.twoStepCode");
    case "backupCode":
      return t("auth.error.backupCode");
    case "password":
      return t("auth.error.invalid");
    case "network":
      return t("auth.error.network");
    case "invalidEmail":
      return t("auth.error.invalidEmail");
    case "invalidCode":
      return t("auth.error.invalidCode");
    case "rateLimited":
      return t("auth.error.rateLimited");
    case "unavailable":
      return t("auth.error.unavailable");
    case "google":
      return t("auth.error.google");
    case "apple":
      return t("auth.error.apple");
    case "invalid":
      return t("auth.error.invalid");
    case "cancelled":
      return t("auth.error.cancelled");
    case "signInExpired":
      return t("auth.error.signInExpired");
    case "codeRejected":
      return t("auth.error.codeRejected");
    case "browser":
      return t("auth.error.browser");
  }
}

/**
 * Sign-in methods the server offers (GET /v1/auth/methods): Apple and Google first, the same size and weight (App
 * Store guideline 4.8 / HIG: Sign in with Apple at least as prominent), then a 6-digit email code (email → code), and
 * dev login only in local development. Platforms that sign in through the system browser
 * (desktop) get that flow instead: open the browser, wait for it, or paste the code it shows.
 */
export type EmailCodeStepProps = {
  email: string;
  busy: SignInBusy | undefined;
  error: ReactNode;
  onChangeEmail: () => void;
  /** Create account: "Continue" and "Resend code" (sign-in: "Sign in" and "Send a new code"). */
  signup?: boolean;
};

/** The 6-digit email code step, shared by sign-in and Create account (verifies with SessionViewModel.verifyCode). */
export function EmailCodeStep({ email, busy, error, onChangeEmail, signup = false }: EmailCodeStepProps) {
  const t = useT();
  const { vm } = useSession();
  const [code, setCode] = useState("");
  const submitCode = () => void vm.verifyCode(code);
  return (
    <Stack gap="4" testID="sign-in-code-step">
      <Stack gap="1">
        {signup ? (
          <Text variant="title2" weight="bold">
            {t("auth.code.title")}
          </Text>
        ) : (
          <Text variant="headline">{t("auth.code.title")}</Text>
        )}
        <Text color="text.secondary">{t("auth.code.sentTo", { email })}</Text>
      </Stack>
      <TextField
        label={t(signup ? "auth.signup.codeLabel" : "auth.code.label")}
        value={code}
        onChange={(v) => setCode(Auth.normalizeEmailCode(v))}
        keyboard="oneTimeCode"
        autoFocus
        onSubmit={submitCode}
        testID="sign-in-code"
      />
      {error}
      <Button label={signup ? t("auth.continue") : t("auth.code.submit")} variant="primary" size="lg" loading={busy === "verifying"} disabled={code.length !== 6} onPress={submitCode} testID="sign-in-code-submit" />
      <Stack direction="horizontal" align="center" justify="between" wrap>
        <Button label={t("auth.code.changeEmail")} variant="ghost" size="sm" onPress={onChangeEmail} testID="sign-in-change-email" />
        <Button label={signup ? t("auth.signup.resend") : t("auth.code.resend")} variant="ghost" size="sm" loading={busy === "sendingCode"} onPress={() => void vm.resendCode()} testID="sign-in-resend" />
      </Stack>
    </Stack>
  );
}

export function SignInForm({ returnTo, defaultDevEmail = "" }: SignInFormProps) {
  const t = useT();
  const { vm, state } = useSession();
  // "Sign in instead" on Create account comes back with the address filled.
  const [email, setEmail] = useState(state.signInEmail ?? "");
  const [devEmail, setDevEmail] = useState(defaultDevEmail);
  const [devName, setDevName] = useState("");
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState("");

  useEffect(() => {
    void vm.loadMethods();
  }, [vm]);
  // Passkey autofill (conditional mediation) on the email field: started with the methods; the browser draws the list.
  const autofill = state.methods?.emailCode === true && state.flow === undefined && state.emailStep === "email" && vm.canUsePasskey;
  useEffect(() => {
    if (autofill) void vm.startPasskeyAutofill();
  }, [autofill, vm]);

  const methods = state.methods;
  const busy = state.busy;
  const error = state.error === undefined ? null : (
    <Stack direction="horizontal" gap="2" align="start" testID="sign-in-error">
      <Icon name="alert" size="icon.sm" color="status.danger" />
      <Text variant="footnote" color="status.danger">
        {signInErrorText(t, state.error)}
      </Text>
    </Stack>
  );

  if (state.browser !== undefined) {
    const waiting = state.browser.waiting;
    const submitPasted = () => void vm.completeBrowserSignIn(pasted);
    return (
      <Stack gap="4" testID="sign-in-browser">
        <Stack gap="1">
          {waiting ? <Text variant="headline">{t("auth.browser.waiting")}</Text> : null}
          <Text color="text.secondary">{waiting ? t("auth.browser.waitingBody") : t("auth.browser.body")}</Text>
        </Stack>
        <Button
          label={waiting ? t("auth.browser.retry") : t("auth.browser.start")}
          icon={waiting ? "reload" : "externalLink"}
          variant={waiting ? "secondary" : "primary"}
          size="lg"
          loading={busy === "browser"}
          onPress={() => void vm.startBrowserSignIn()}
          testID="sign-in-browser-start"
        />
        {error}
        {pasting ? (
          <Stack gap="3" testID="sign-in-paste">
            <TextField
              label={t("auth.browser.pasteLabel")}
              placeholder={t("auth.browser.pastePlaceholder")}
              value={pasted}
              onChange={setPasted}
              autoFocus
              onSubmit={submitPasted}
              testID="sign-in-paste-code"
            />
            <Button
              label={t("auth.code.submit")}
              variant="primary"
              loading={busy === "verifying"}
              disabled={pasted.trim() === ""}
              onPress={submitPasted}
              testID="sign-in-paste-submit"
            />
          </Stack>
        ) : (
          <Stack direction="horizontal" justify="center">
            <Button label={t("auth.browser.paste")} icon="key" variant="ghost" size="sm" onPress={() => setPasting(true)} testID="sign-in-paste-open" />
          </Stack>
        )}
      </Stack>
    );
  }

  const flow = state.flow;
  if (flow !== undefined) {
    const problem = flow.step === "resetPassword" ? flow.problem : undefined;
    const flowError = state.error === undefined ? undefined : (flowErrorText(t, state.error, state.lockedMinutes, problem) ?? signInErrorText(t, state.error));
    switch (flow.step) {
      case "password":
        return <PasswordStep vm={vm} flow={flow} busy={busy} error={flowError} />;
      case "twoFactor":
        return <TwoFactorStep vm={vm} flow={flow} busy={busy} error={flowError} />;
      case "resetCode":
        return <ResetCodeStep vm={vm} flow={flow} busy={busy} error={flowError} />;
      case "resetPassword":
        return <ResetPasswordStep vm={vm} flow={flow} busy={busy} error={flowError} />;
      case "resetDone":
        return <ResetDoneStep vm={vm} flow={flow} />;
      case "passkey":
        return (
          <Stack gap="3">
            <PasskeyStep vm={vm} flow={flow} />
            <StepError text={flowError} />
          </Stack>
        );
    }
  }

  if (methods === undefined) {
    return state.methodsFailed ? (
      <Stack gap="3">
        <Text color="text.secondary">{t("auth.methods.failed")}</Text>
        <Button label={t("common.retry")} icon="reload" onPress={() => void vm.loadMethods()} testID="sign-in-retry" />
      </Stack>
    ) : (
      <Text color="text.secondary">{t("common.loading")}</Text>
    );
  }

  if (state.emailStep === "code" && state.codeEmail !== undefined) {
    return <EmailCodeStep email={state.codeEmail} busy={busy} error={error} onChangeEmail={() => vm.editEmail()} />;
  }

  const sendCode = () => void vm.sendCode(email);
  const apple = methods.apple === true && vm.canSignInWithApple;
  const passkey = vm.canUsePasskey;
  const provider = apple || methods.google || passkey;
  const devOnly = methods.devLogin && !provider && !methods.emailCode;
  return (
    <Stack gap="4" testID="sign-in-methods">
      {provider ? (
        <Stack gap="3">
          {passkey ? (
            <Button label={t("auth.passkey.signIn")} icon="key" variant="secondary" size="lg" loading={busy === "passkey"} onPress={() => void vm.signInWithPasskey()} testID="sign-in-passkey" />
          ) : null}
          {apple ? (
            <Button label={t("auth.apple")} brand="apple" size="lg" loading={busy === "apple"} onPress={() => void vm.signInWithApple(returnTo)} testID="sign-in-apple" />
          ) : null}
          {methods.google ? (
            <Button label={t("auth.google")} brand="google" size="lg" loading={busy === "google"} onPress={() => void vm.signInWithGoogle(returnTo)} testID="sign-in-google" />
          ) : null}
        </Stack>
      ) : null}
      {provider && methods.emailCode ? (
        <Stack direction="horizontal" gap="3" align="center">
          <Stack grow>
            <Divider />
          </Stack>
          <Text variant="footnote" color="text.tertiary">
            {t("auth.or")}
          </Text>
          <Stack grow>
            <Divider />
          </Stack>
        </Stack>
      ) : null}
      {methods.emailCode ? (
        <Stack gap="3">
          <TextField
            label={t("auth.email")}
            placeholder={t("auth.emailPlaceholder")}
            value={email}
            onChange={setEmail}
            keyboard="email"
            contentType="username"
            passkeyAutofill={passkey}
            autoFocus={!provider}
            onSubmit={sendCode}
            testID="sign-in-email"
          />
          <Button
            label={t("auth.emailCode.send")}
            icon="mail"
            variant={provider ? "secondary" : "primary"}
            size="lg"
            loading={busy === "sendingCode"}
            disabled={email.trim() === ""}
            onPress={sendCode}
            testID="sign-in-send-code"
          />
          {vm.canUsePassword ? (
            <Stack direction="horizontal" justify="center">
              <Button label={t("auth.password.use")} variant="ghost" size="sm" onPress={() => vm.passwordInstead(email)} testID="sign-in-use-password" />
            </Stack>
          ) : null}
        </Stack>
      ) : null}
      {error}
      {methods.devLogin ? (
        <Stack gap="3" padding={devOnly ? "0" : "4"} radius="lg" border={devOnly ? undefined : "border.subtle"} testID="sign-in-dev">
          <Stack gap="0.5">
            <Text variant="footnote" weight="semibold" color="text.secondary">
              {t("auth.dev.title")}
            </Text>
            <Text variant="caption" color="text.tertiary">
              {t("auth.dev.hint")}
            </Text>
          </Stack>
          <TextField label={t("auth.email")} value={devEmail} onChange={setDevEmail} keyboard="email" onSubmit={() => void vm.devSignIn(devEmail, devName)} testID="sign-in-dev-email" />
          <TextField label={t("auth.name")} value={devName} onChange={setDevName} onSubmit={() => void vm.devSignIn(devEmail, devName)} testID="sign-in-dev-name" />
          <Button
            label={busy === "dev" ? t("auth.signingIn") : t("auth.continue")}
            variant={devOnly ? "primary" : "secondary"}
            loading={busy === "dev"}
            disabled={devEmail.trim() === ""}
            onPress={() => void vm.devSignIn(devEmail, devName)}
            testID="sign-in-dev-submit"
          />
        </Stack>
      ) : null}
    </Stack>
  );
}
